// Runtime definitions loader.
//
// Per-system achievement definitions live in `public/cheevos/v2/<sys>.json`
// (written by gen-definitions.mjs at deploy time). The client fetches
// them on demand so they're not part of the main bundle — only the
// active system's file is downloaded.
//
// The v2 path is the trigger-DSL skew guard: old cached clients keep
// fetching the frozen legacy /cheevos/<sys>.json files, so they never see
// triggers with fields their engine would silently mis-evaluate.

import { mergedSystems } from '../../shared/system-merge.js';

async function fetchOne(systemId) {
  const res = await fetch(`/cheevos/v2/${systemId}.json`, { cache: 'default' });
  if (!res.ok) {
    throw new Error(`cheevos fetch failed: /cheevos/v2/${systemId}.json → ${res.status}`);
  }
  const data = await res.json();
  if (!Array.isArray(data?.games)) {
    throw new Error(`cheevos payload invalid: /cheevos/v2/${systemId}.json`);
  }
  return data;
}

/**
 * Default loader used in production. Passed to the AchievementEngine
 * via its `loader` constructor arg. Tests should inject their own
 * synchronous stub instead of hitting this path.
 *
 * gb and gbc are one system: definitions are generated into separate
 * /cheevos/v2/gb.json and gbc.json files, but a GBC game plays under `gb`.
 * For a merged system we fetch every member file and concatenate their
 * `games` so the engine's hash index covers both — otherwise a GBC game
 * played under gb has no live achievement detection or badges.
 *
 * The optional `failedSystems` retry hint limits a merged request to its
 * missing members. The returned games and status describe that request;
 * the engine combines them with its previously successful definitions.
 */
export async function fetchSystemDefinitions(systemId, { failedSystems: retrySystems } = {}) {
  const members = mergedSystems(systemId);
  if (members.length === 1) return fetchOne(systemId);
  const requested = Array.isArray(retrySystems)
    ? members.filter(member => retrySystems.includes(member)) : [];
  const systems = requested.length ? requested : members;

  const parts = await Promise.allSettled(systems.map(fetchOne));
  const ok = parts.filter(part => part.status === 'fulfilled').map(part => part.value);
  const failedSystems = systems.filter((_system, index) => parts[index].status === 'rejected');
  if (!ok.length) {
    throw Object.assign(new Error(`cheevos fetch failed for merged systems: ${systems.join(',')}`), { failedSystems });
  }
  return {
    system: systemId,
    games: ok.flatMap((d) => (Array.isArray(d?.games) ? d.games : [])),
    loadStatus: failedSystems.length ? 'partial' : 'complete',
    failedSystems,
  };
}
